// Player de vídeo explicativo. Aceita URLs de YouTube, Vimeo ou MP4.
// Antes do vídeo estar pronto, mostra placeholder elegante.

import { useState } from 'react'
import { VIDEOS, temVideo, type AudiencaVideo } from '../lib/videos'

type Props = {
  audiencia: AudiencaVideo
  /** Cor de destaque do botão play e ações. Default = roxo paciente. */
  cor?: string
  /** Texto do botão de fallback (placeholder, sem URL). */
  textoPlaceholder?: string
}

export default function VideoExplicativo({
  audiencia,
  cor = '#a8e6f0',
  textoPlaceholder,
}: Props) {
  const [aberto, setAberto] = useState(false)
  const video = VIDEOS[audiencia]
  const disponivel = temVideo(audiencia)

  // Detecta tipo de URL pra escolher player adequado
  const tipoUrl = (() => {
    if (!video.url) return 'placeholder'
    if (video.url.includes('youtube.com') || video.url.includes('youtu.be')) return 'youtube'
    if (video.url.includes('vimeo.com')) return 'vimeo'
    return 'mp4'
  })()

  return (
    <>
      {/* Card grande clicável — substitui mockups dos heroes */}
      <button
        type="button"
        onClick={() => disponivel && setAberto(true)}
        disabled={!disponivel}
        className={`group relative w-full overflow-hidden rounded-3xl border-2 transition ${
          disponivel
            ? 'border-white/25 hover:border-white/40 cursor-pointer'
            : 'border-white/15 cursor-default'
        }`}
        style={{
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
          backdropFilter: 'blur(8px)',
          aspectRatio: '16 / 9',
        }}
        aria-label={disponivel ? `Assistir vídeo: ${video.titulo}` : 'Vídeo em breve'}
      >
        {/* Glow do botão play */}
        <div
          className="absolute inset-0 opacity-50 group-hover:opacity-80 transition"
          style={{
            background: `radial-gradient(circle at center, ${cor}33 0%, transparent 60%)`,
          }}
        />

        {/* Centro: botão play */}
        <div className="absolute inset-0 flex items-center justify-center flex-col gap-4">
          <div
            className={`w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center transition shadow-2xl ${
              disponivel ? 'group-hover:scale-110' : 'opacity-60'
            }`}
            style={{ background: cor, boxShadow: `0 12px 40px ${cor}55` }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="currentColor"
              style={{ color: '#0d4a60', marginLeft: 4 }}
              aria-hidden="true"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>

          <div className="text-center px-4">
            <div className="text-base sm:text-lg font-bold text-white mb-1">
              {video.titulo}
            </div>
            <div className="text-xs text-white/70 flex items-center justify-center gap-2">
              {disponivel ? (
                <>
                  <span>▶ Assistir</span>
                  <span>·</span>
                  <span>{video.duracao}</span>
                </>
              ) : (
                <span>{textoPlaceholder || 'Vídeo em breve'}</span>
              )}
            </div>
          </div>
        </div>

        {/* Borda interior brilhante */}
        <div
          className="absolute inset-0 rounded-3xl pointer-events-none"
          style={{
            boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.1), inset 0 0 60px ${cor}10`,
          }}
        />
      </button>

      {/* Modal com player */}
      {aberto && disponivel && (
        <div
          className="fixed inset-0 bg-black/85 z-[100] flex items-center justify-center p-4"
          onClick={() => setAberto(false)}
        >
          <div
            className="relative w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setAberto(false)}
              className="absolute -top-12 right-0 text-white/80 hover:text-white text-3xl leading-none"
              aria-label="Fechar"
            >
              ×
            </button>

            <div
              className="rounded-2xl overflow-hidden bg-black shadow-2xl"
              style={{ aspectRatio: '16 / 9' }}
            >
              {tipoUrl === 'youtube' && (
                <iframe
                  src={`${video.url}?autoplay=1&rel=0`}
                  title={video.titulo}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full border-0"
                />
              )}
              {tipoUrl === 'vimeo' && (
                <iframe
                  src={`${video.url}?autoplay=1`}
                  title={video.titulo}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full border-0"
                />
              )}
              {tipoUrl === 'mp4' && (
                <video
                  src={video.url}
                  title={video.titulo}
                  controls
                  autoPlay
                  className="w-full h-full"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
